/**
 * @module linkage/qbFields/qbLinkage/handlers/sortBy
 * @description X/Y 轴排序 — `sortBy` / `chartSorts` / `customMetricSort` 的 QB 联动
 *
 * 条形图 Y 轴排序复用 X 轴同一套上传字段（见 YAxisSetting → XAxisSortBy）。
 *
 * ## QB 删指标
 *
 * 与图例 `seriesSortsHandler` 一致：处于「按指标排序」或已有 chartSorts/customMetricSort 时，
 * 删任一指标后整组回默认排序，避免 CustomMetricSort 在空数组时自动回填剩余指标。
 * 纯「手动排序」且仅有 xAxisValues 时不重置（删指标不影响轴类目顺序配置）。
 *
 * @see settingGroups/XAxisSetting/XAxisSortBy/useXAxisSortBy.ts
 * @see handlers/seriesSorts.ts
 */

import type { IQBLinkageHandler } from '../types';
import { SortByEnum } from '../../../../settingGroups/common/SortByPanel/SortType';
import {
  buildDefaultSortPayload,
  toSortUiMode,
} from '../../../../settingGroups/XAxisSetting/XAxisSortBy/utils';
import {
  hasAnyConfigInLayers,
  hasNonEmptyArrayInLayers,
  metricLikeRemovedFields,
  patchBoth,
} from './shared';
import {
  migrateSeriesSorts,
  type ISeriesSortRow,
} from './seriesSorts';

const AXIS_SORT_KEYS = [
  'sortBy',
  'chartSorts',
  'customMetricSort',
  'xAxisValues',
] as const;

/**
 * QB 删指标时是否应整组回默认排序（仅指标排序路径；不碰纯手动排序 / 字段排序）。
 *
 * 字段排序（datasetField）也写 chartSorts，但引用的是整表/自定义字段而非 QB 指标；
 * 删 QB 指标时不得误伤，自定义字段删除走 A1（datasetFieldSortSync）。
 */
export function shouldResetAxisSortOnMetricRemoved(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): boolean {
  const sortByRaw = String(draft.sortBy ?? base.sortBy ?? '');
  const mode = toSortUiMode(sortByRaw);
  if (mode === SortByEnum.DatasetFieldSort) {
    return false;
  }
  if (mode === SortByEnum.Metric) {
    return true;
  }
  return (
    hasNonEmptyArrayInLayers(base, draft, 'chartSorts')
    || hasNonEmptyArrayInLayers(base, draft, 'customMetricSort')
  );
}

function migrateAxisMetricSortRows(
  list: ISeriesSortRow[] | undefined,
  renames: Parameters<typeof migrateSeriesSorts>[1],
): ISeriesSortRow[] | undefined {
  return migrateSeriesSorts(list, renames);
}

export const sortByHandler: IQBLinkageHandler = {
  id: 'sortBy',
  phase: 'prune',
  keys: [...AXIS_SORT_KEYS],
  subscribe: ['metricRemoved', 'rawColumnRemoved'],
  apply(changeSet, ctx) {
    const removed = metricLikeRemovedFields(changeSet);
    if (!removed.length) {
      return;
    }
    if (!hasAnyConfigInLayers(ctx.base, ctx.draft, [...AXIS_SORT_KEYS])) {
      return;
    }
    if (!shouldResetAxisSortOnMetricRemoved(ctx.base, ctx.draft)) {
      return;
    }
    const resetPatch = buildDefaultSortPayload();
    return patchBoth({ ...resetPatch }, { ...resetPatch });
  },
};

/** Phase migrate：QB 字段身份改名时迁移 chartSorts / customMetricSort */
export const sortByMigrateHandler: IQBLinkageHandler = {
  id: 'sortBy-migrate',
  phase: 'migrate',
  keys: ['chartSorts', 'customMetricSort'],
  subscribe: ['fieldIdentityRenamed'],
  apply(changeSet, ctx) {
    const hasChartSorts = hasNonEmptyArrayInLayers(
      ctx.base,
      ctx.draft,
      'chartSorts',
    );
    const hasCustom = hasNonEmptyArrayInLayers(
      ctx.base,
      ctx.draft,
      'customMetricSort',
    );
    if (!hasChartSorts && !hasCustom) {
      return;
    }

    const baseChartNext = migrateAxisMetricSortRows(
      ctx.base.chartSorts as ISeriesSortRow[] | undefined,
      changeSet.renamed,
    );
    const draftChartNext = migrateAxisMetricSortRows(
      ctx.draft.chartSorts as ISeriesSortRow[] | undefined,
      changeSet.renamed,
    );
    const baseCustomNext = migrateAxisMetricSortRows(
      ctx.base.customMetricSort as ISeriesSortRow[] | undefined,
      changeSet.renamed,
    );
    const draftCustomNext = migrateAxisMetricSortRows(
      ctx.draft.customMetricSort as ISeriesSortRow[] | undefined,
      changeSet.renamed,
    );

    const basePatch: Record<string, unknown> = {};
    const draftPatch: Record<string, unknown> = {};

    if (baseChartNext !== undefined) {
      basePatch.chartSorts = baseChartNext;
      basePatch.customMetricSort = baseChartNext;
    } else if (baseCustomNext !== undefined) {
      basePatch.chartSorts = baseCustomNext;
      basePatch.customMetricSort = baseCustomNext;
    }

    if (draftChartNext !== undefined) {
      draftPatch.chartSorts = draftChartNext;
      draftPatch.customMetricSort = draftChartNext;
    } else if (draftCustomNext !== undefined) {
      draftPatch.chartSorts = draftCustomNext;
      draftPatch.customMetricSort = draftCustomNext;
    }

    if (!Object.keys(basePatch).length && !Object.keys(draftPatch).length) {
      return;
    }
    return patchBoth(
      Object.keys(basePatch).length ? basePatch : undefined,
      Object.keys(draftPatch).length ? draftPatch : undefined,
    );
  },
};
